import { ArrowLeft } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const SugarLoadDetail = () => {
  const navigate = useNavigate();

  // Today's data
  const todaySugar = 45; // grams
  const todayCarbs = 180; // grams
  const sugarToCarbs = ((todaySugar / todayCarbs) * 100).toFixed(1);

  const sugarComposition = [
    { name: "Sugar", value: todaySugar, color: "hsl(var(--chart-4))" },
    {
      name: "Other Carbs",
      value: todayCarbs - todaySugar,
      color: "hsl(var(--chart-2))",
    },
  ];

  const weeklyData = [
    { day: "Mon", sugar: 42, carbs: 175, ratio: 24 },
    { day: "Tue", sugar: 38, carbs: 160, ratio: 23.8 },
    { day: "Wed", sugar: 52, carbs: 190, ratio: 27.4 },
    { day: "Thu", sugar: 35, carbs: 165, ratio: 21.2 },
    { day: "Fri", sugar: 48, carbs: 185, ratio: 25.9 },
    { day: "Sat", sugar: 55, carbs: 200, ratio: 27.5 },
    { day: "Sun", sugar: 45, carbs: 180, ratio: 25 },
  ];

  const monthlyData = [
    { week: "Week 1", sugar: 44, carbs: 175, ratio: 25.1 },
    { week: "Week 2", sugar: 40, carbs: 170, ratio: 23.5 },
    { week: "Week 3", sugar: 48, carbs: 185, ratio: 25.9 },
    { week: "Week 4", sugar: 42, carbs: 178, ratio: 23.6 },
  ];

  // ratio = sugar / carbs (as decimal, e.g. 0.10 = 10%)
  const sugarRatio = todaySugar / todayCarbs;

  const getSugarLoadStatus = (ratio: number) => {
    if (ratio <= 0.1) {
      return { status: "Good", color: "text-success" };
    }
    if (ratio <= 0.2) {
      return { status: "Moderate", color: "text-warning" };
    }
    return { status: "Needs Improvement", color: "text-destructive" };
  };

  const status = getSugarLoadStatus(sugarRatio);

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/home")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Sugar Load</h1>
          <div className="w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="from-chart-4/10 to-chart-4/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Sugar Load
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">
              {sugarRatio.toFixed(2)}
            </div>
            <p className={`text-sm font-semibold ${status.color}`}>
              {status.status}
            </p>
            <div className="mt-4 flex justify-center gap-8">
              <div className="text-center">
                <div className="text-chart-4 text-2xl font-bold">
                  {todaySugar}g
                </div>
                <p className="text-xs text-muted-foreground">Sugar</p>
              </div>
              <div className="text-center">
                <div className="text-chart-2 text-2xl font-bold">
                  {todayCarbs}g
                </div>
                <p className="text-xs text-muted-foreground">Total Carbs</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Sugar vs Carbs Pie Chart */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-4 text-center font-bold text-foreground">
            Sugar vs Carbs Breakdown
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={sugarComposition}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {sugarComposition.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                }}
                formatter={(value: number) => [`${value}g`, ""]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 flex justify-center gap-4">
            {sugarComposition.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-muted-foreground">
                  {item.name}: {item.value}g
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* What is Sugar Load */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Sugar Load?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Sugar Load measures the percentage of your total carbohydrate intake
            that comes from sugars. A lower sugar-to-carbs ratio indicates a
            healthier diet with more complex carbohydrates, which provides
            sustained energy and better blood sugar control.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="text-success font-semibold">≤ 0.10</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Moderate</span>
              <span className="text-warning font-semibold">0.10 - 0.20</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Needs Improvement</span>
              <span className="font-semibold text-destructive">Above 0.20</span>
            </div>
          </div>
        </Card>

        {/* Tabs for Weekly/Monthly */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="m-4 grid w-full grid-cols-2 p-1">
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="weekly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={weeklyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="day"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                    formatter={(value: number, name: string) => [
                      `${value}${name === "ratio" ? "%" : "g"}`,
                      name === "sugar"
                        ? "Sugar"
                        : name === "carbs"
                          ? "Carbs"
                          : "Ratio",
                    ]}
                  />
                  <Bar
                    dataKey="sugar"
                    fill="hsl(var(--chart-4))"
                    radius={[4, 4, 0, 0]}
                    name="sugar"
                  />
                  <Bar
                    dataKey="carbs"
                    fill="hsl(var(--chart-2))"
                    radius={[4, 4, 0, 0]}
                    name="carbs"
                  />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-2 flex justify-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="bg-chart-4 h-3 w-3 rounded-full" />
                  <span className="text-xs text-muted-foreground">
                    Sugar (g)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-chart-2 h-3 w-3 rounded-full" />
                  <span className="text-xs text-muted-foreground">
                    Carbs (g)
                  </span>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="monthly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={monthlyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="week"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="sugar"
                    stroke="hsl(var(--chart-4))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--chart-4))" }}
                    name="Sugar (g)"
                  />
                  <Line
                    type="monotone"
                    dataKey="carbs"
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--chart-2))" }}
                    name="Carbs (g)"
                  />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-2 flex justify-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="bg-chart-4 h-3 w-3 rounded-full" />
                  <span className="text-xs text-muted-foreground">
                    Sugar (g)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-chart-2 h-3 w-3 rounded-full" />
                  <span className="text-xs text-muted-foreground">
                    Carbs (g)
                  </span>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Tips to Reduce Sugar Load */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            Tips to Reduce Sugar Load
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-chart-2 mt-0.5">•</span>
              <span>Choose whole grains over refined carbohydrates</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2 mt-0.5">•</span>
              <span>
                Replace sugary drinks with water or unsweetened beverages
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2 mt-0.5">•</span>
              <span>Eat whole fruits instead of fruit juices</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-2 mt-0.5">•</span>
              <span>Check labels for hidden sugars in processed foods</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default SugarLoadDetail;
